import { useImportImage } from "@features/images/use-import-image";
import { useLoras } from "@features/loras/use-loras";
import {
  ArrowUpTrayIcon,
  CameraIcon,
  HomeIcon,
} from "@heroicons/react/16/solid";
import { HeartFilledIcon } from "@radix-ui/react-icons";
import { IconButton, Spinner } from "@radix-ui/themes";
import { useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ClothIcon } from "./icons/cloth";
import { HaircutIcon } from "./icons/haircut";

export const MobileBottomBar = () => {
  const { site, id } = useParams<{ site: string; id?: string }>();
  const { pathname } = useLocation();
  const { loras } = useLoras();
  const defaultProjectId = pathname.startsWith("/project/") ? id : "none";

  // Check if we're on a page where we don't want the bar
  const isLoraPage = pathname.includes("/lora/");
  const isWelcomePage = pathname.includes("/welcome");
  const isStudio = pathname.includes("/studio");

  // Check if we're on the home page
  const isHomePage = pathname === `/${site}/home` || pathname === `/${site}/`;

  // Import functionality
  const { importImage, loading: importLoading } = useImportImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Don't render the bar on lora or welcome pages
  if (isLoraPage || isWelcomePage || isStudio) {
    return null;
  }

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 10MB");
      return;
    }

    try {
      await importImage(
        file,
        defaultProjectId !== "none" ? defaultProjectId : undefined,
      );
      toast.success("Image imported successfully!");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import image");
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 pb-safe mobile-safe-bottom">
        <div className="bg-white dark:bg-slate-950 backdrop-blur-lg bg-opacity-50 rounded-full mx-auto max-w-xs w-full shadow border-t dark:border border-slate-100 dark:border-slate-900">
          <div className="flex items-center justify-between mx-auto p-4">
            {/* Home or Favorites - depending on current page */}
            {isHomePage ? (
              <Link to={`/${site}/home/search/favorite:1`}>
                <IconButton
                  variant="ghost"
                  size="3"
                  color="red"
                  className="!rounded-full h-6 w-6"
                >
                  <HeartFilledIcon className="w-5 h-5" />
                </IconButton>
              </Link>
            ) : (
              <Link to={`/${site}/home`}>
                <IconButton
                  variant="ghost"
                  size="3"
                  color="gray"
                  className="!rounded-full h-6 w-6"
                >
                  <HomeIcon className="w-5 h-5" />
                </IconButton>
              </Link>
            )}
            {/* Upload */}
            <IconButton
              variant="ghost"
              size="3"
              color="gray"
              className="!rounded-full h-6 w-6"
              onClick={() => fileInputRef.current?.click()}
              disabled={importLoading}
            >
              {importLoading ? (
                <Spinner className="w-5 h-5" />
              ) : (
                <ArrowUpTrayIcon className="w-5 h-5" />
              )}
            </IconButton>
            <div className="w-1" />
            {/* Take Photo - Center, larger, blue background */}
            <Link
              to={
                !loras?.data?.length
                  ? `/upload?free=1`
                  : `/${site}/templates?project=${defaultProjectId}`
              }
            >
              <IconButton
                variant="solid"
                color="indigo"
                size="4"
                className="!rounded-full !w-16 !h-16 -mt-1 shadow-lg absolute mx-auto left-0 right-0 top-0 bottom-0"
              >
                <CameraIcon className="w-7 h-7" />
              </IconButton>
            </Link>
            {/* Last tool used - Haircut for now */}
            <Link
              to={`/${site}/templates?mode=haircut&project=${defaultProjectId}`}
            >
              <IconButton
                variant="ghost"
                size="3"
                color="gray"
                className="!rounded-full h-6 w-6"
              >
                <HaircutIcon className="w-5 h-5" />
              </IconButton>
            </Link>
            {/* More menu */}{" "}
            <Link
              to={`/${site}/templates?mode=tryon&project=${defaultProjectId}`}
            >
              <IconButton
                variant="ghost"
                size="3"
                color="gray"
                className="!rounded-full h-6 w-6"
              >
                <ClothIcon className="w-5 h-5" />
              </IconButton>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
